import React, { useState, useEffect } from 'react';
import './DigitalTransformationAssessment.css'

const DigitalTransformationAssessment = ({ assessmentId, onSaveSuccess }) => {
    const [activeTab, setActiveTab] = useState('hardware');
    const [formData, setFormData] = useState({
        id: assessmentId || null, // เพิ่มฟิลด์ id เพื่อเก็บค่า assessmentId
        hardware: {},
        network: {},
        software: {},
        dataInfrastructure: {},
        budget: {
            hardware: '',
            network: '',
            software: '',
            dataInfrastructure: '',
            total: '',
            hardwareDetails: '',
            networkDetails: '',
            softwareDetails: '',
            dataInfrastructureDetails: ''
        },
        assessmentName: '',
        companyName: '',
        assessorName: '',
        position: '',
        assessmentDate: new Date().toISOString().split('T')[0],
        notes: ''
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');

    const apiUrl = 'https://internalassessmentsyngroups.com/digital-transformation/api'; // Update with your API URL

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleInputChange = (section, category, item, field, value) => {
        setFormData(prevData => ({
            ...prevData,
            [section]: {
                ...prevData[section],
                [category]: {
                    ...(prevData[section][category] || {}),
                    [item]: {
                        ...(prevData[section][category]?.[item] || {}),
                        [field]: value
                    }
                }
            }
        }));
    };

    // Initialize form data with default target score of 5
    useEffect(() => {
        const initializedData = { ...formData };

        // ตั้งค่า id ให้ตรงกับ assessmentId ที่รับมา
        initializedData.id = assessmentId || null;

        // Loop through all sections and set default target of 5
        Object.keys(sections).forEach(sectionKey => {
            initializedData[sectionKey] = initializedData[sectionKey] || {};

            Object.keys(sections[sectionKey].categories).forEach(categoryKey => {
                initializedData[sectionKey][categoryKey] = initializedData[sectionKey][categoryKey] || {};

                sections[sectionKey].categories[categoryKey].items.forEach(item => {
                    initializedData[sectionKey][categoryKey][item] = initializedData[sectionKey][categoryKey][item] || {};

                    // Only set target if it's not already set
                    if (!initializedData[sectionKey][categoryKey][item].target) {
                        initializedData[sectionKey][categoryKey][item].target = 5;
                    }
                });
            });
        });

        setFormData(initializedData);

        // If assessmentId is provided, fetch existing data
        if (assessmentId) {
            fetchAssessmentData(assessmentId);
        }
    }, [assessmentId]);

    const fetchAssessmentData = async (id) => {
        try {
            const response = await fetch(`https://internalassessmentsyngroups.com/digital-transformation/api/get_assessment_data.php?id=${id}`);
            const responseData = await response.json();

            if (responseData.success) {
                const { assessment, budget, items } = responseData;

                // Set assessment metadata
                const updatedFormData = {
                    ...formData,
                    id: assessment.id, // เก็บค่า id ของแบบประเมิน
                    assessmentName: assessment.assessment_name,
                    companyName: assessment.company_name,
                    assessorName: assessment.assessor_name,
                    position: assessment.position,
                    assessmentDate: assessment.assessment_date,
                    notes: assessment.notes || '',
                    budget: {
                        hardware: budget.hardware_budget || '',
                        network: budget.network_budget || '',
                        software: budget.software_budget || '',
                        dataInfrastructure: budget.data_infrastructure_budget || '',
                        hardwareDetails: budget.hardware_details || '',
                        networkDetails: budget.network_details || '',
                        softwareDetails: budget.software_details || '',
                        dataInfrastructureDetails: budget.data_infrastructure_details || '',
                        total: (parseFloat(budget.hardware_budget) +
                            parseFloat(budget.network_budget) +
                            parseFloat(budget.software_budget) +
                            parseFloat(budget.data_infrastructure_budget)).toString()
                    }
                };

                // Process items and structure them in the form data
                items.forEach(item => {
                    const { section, category, item: itemName, ...itemData } = item;

                    if (!updatedFormData[section]) {
                        updatedFormData[section] = {};
                    }

                    if (!updatedFormData[section][category]) {
                        updatedFormData[section][category] = {};
                    }

                    updatedFormData[section][category][itemName] = {
                        currentStatus: parseInt(itemData.current_status) || 0,
                        target: parseInt(itemData.target) || 5,
                        priority: itemData.priority || '',
                        age: itemData.age || '',
                        details: itemData.details || '',
                        targetDescription: itemData.target_description || '',
                        suggestions: itemData.suggestions || '',
                        responsible: itemData.responsible || ''
                    };

                    // Add software-specific fields if applicable
                    if (section === 'software') {
                        updatedFormData[section][category][itemName].licenses = itemData.licenses || '';
                        updatedFormData[section][category][itemName].version = itemData.version || '';
                    }
                });

                setFormData(updatedFormData);
            } else {
                setError('Failed to load assessment data: ' + responseData.message);
            }
        } catch (err) {
            setError('Error loading assessment data. Please check your connection.');
            console.error(err);
        }
    };

    const handleBudgetChange = (category, value) => {
        setFormData(prevData => ({
            ...prevData,
            budget: {
                ...prevData.budget,
                [category]: value
            }
        }));
    };

    const handleMetadataChange = (field, value) => {
        setFormData(prevData => ({
            ...prevData,
            [field]: value
        }));
    };

    // Calculate total budget when any budget field changes
    const calculateTotalBudget = () => {
        const { hardware, network, software, dataInfrastructure } = formData.budget;

        // Convert string values to numbers, handling empty or non-numeric values
        const hardwareValue = parseFloat(hardware) || 0;
        const networkValue = parseFloat(network) || 0;
        const softwareValue = parseFloat(software) || 0;
        const dataInfrastructureValue = parseFloat(dataInfrastructure) || 0;

        // Calculate total and update total in formData
        const total = hardwareValue + networkValue + softwareValue + dataInfrastructureValue;

        // Only update if the total has changed
        if (total !== parseFloat(formData.budget.total)) {
            setFormData(prevData => ({
                ...prevData,
                budget: {
                    ...prevData.budget,
                    total: total.toString()
                }
            }));
        }

        return total;
    };

    const handleSaveAssessment = async () => {
        // Validate form fields
        if (!formData.assessmentName || !formData.companyName || !formData.assessorName || !formData.position || !formData.assessmentDate) {
            setError('Please fill in all required fields in the assessment information section.');
            setActiveTab('summary');
            return;
        }

        try {
            setSaving(true);
            setError(null);

            console.log("Saving assessment with ID:", formData.id); // ตรวจสอบว่า id ถูกส่งไปด้วย

            const response = await fetch(`https://internalassessmentsyngroups.com/digital-transformation/api/save_assessment.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const responseData = await response.json();

            if (responseData.success) {
                setSuccessMessage('Assessment saved successfully!');

                // If onSaveSuccess callback provided, call it with the assessment ID
                if (onSaveSuccess) {
                    onSaveSuccess(responseData.assessmentId);
                }
            } else {
                setError('Failed to save assessment: ' + responseData.message);
            }
        } catch (err) {
            setError('Error saving assessment. Please check your connection.');
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const ScoreBar = ({ value, onChange, section, category, item, field }) => {
        return (
            <div className="flex space-x-1">
                {[1, 2, 3, 4, 5].map((score) => (
                    <button
                        key={score}
                        className={`w-8 h-8 flex items-center justify-center border ${value === score ? 'bg-blue-500 text-white' : 'bg-gray-100'
                            }`}
                        onClick={() => onChange(section, category, item, field, score)}
                    >
                        {score}
                    </button>
                ))}
            </div>
        );
    };

    const PrioritySelect = ({ value, onChange, section, category, item }) => {
        return (
            <div className="flex space-x-1">
                {['H', 'M', 'L'].map((priority) => (
                    <button
                        key={priority}
                        className={`w-8 h-8 flex items-center justify-center border ${value === priority ? 'bg-blue-500 text-white' : 'bg-gray-100'
                            }`}
                        onClick={() => onChange(section, category, item, 'priority', priority)}
                    >
                        {priority}
                    </button>
                ))}
            </div>
        );
    };

    // Define assessment sections with their items
    const sections = {
        hardware: {
            title: "1. Hardware",
            categories: {
                computers: {
                    title: "เครื่องคอมพิวเตอร์",
                    items: [
                        "PC/Laptop สำหรับพนักงานทั่วไป",
                        "สำหรับงานออกแบบ/กราฟิก",
                        "สำหรับ Programmers",
                        "สำหรับผู้บริหาร",
                        "สำหรับงานภาคสนาม"
                    ]
                },
                servers: {
                    title: "Server",
                    items: [
                        "Web Server",
                        "Database Server",
                        "File Server",
                        "Application Server"
                    ]
                },
                accessories: {
                    title: "อุปกรณ์เสริม",
                    items: [
                        "เครื่องพิมพ์",
                        "เครื่องแสกนเอกสาร",
                        "อุปกรณ์จัดเก็บข้อมูล",
                        "UPS"
                    ]
                }
            }
        },
        network: {
            title: "2. Network",
            categories: {
                internet: {
                    title: "Internet",
                    items: [
                        "Internet speed <ขาเข้า-ขาออก>",
                        "ความเสถียร <Uptime>",
                        "ผู้ให้บริการสำรอง <มี-ไม่มี>"
                    ]
                },
                lan: {
                    title: "LAN",
                    items: [
                        "เครือข่ายภายใน <ความเร็ว>",
                        "จำนวนจุดเชื่อมต่อ <จำนวน>",
                        "WiFi <ครอบคลุม>"
                    ]
                },
                networkEquipment: {
                    title: "อุปกรณ์เครือข่าย",
                    items: [
                        "Router <จำนวน><ประเภท>",
                        "Switch <จำนวน><ประเภท>",
                        "Firewall ประเภท"
                    ]
                },
                security: {
                    title: "ความปลอดภัย",
                    items: [
                        "การป้องกันการบุกรุก <ระบบ>",
                        "การเข้ารหัสข้อมูล <ประเภท>",
                        "การตรวจสอบการใช้งาน <ระบบ>"
                    ]
                }
            }
        },
        software: {
            title: "3. Software",
            categories: {
                operatingSystems: {
                    title: "ระบบปฏิบัติการ",
                    items: [
                        "Windows",
                        "macOS",
                        "Linux/Unix"
                    ]
                },
                officeApps: {
                    title: "Software สำนักงาน",
                    items: [
                        "Microsoft Office",
                        "Google Workspace",
                        "อื่นๆ"
                    ]
                },
                firmware: {
                    title: "Firmware",
                    items: [
                        "BIOS/UEFI",
                        "Network Device Firmware",
                        "Embedded Device Firmware",
                        "IoT Device Firmware"
                    ]
                },
                lowLevelLanguages: {
                    title: "Low-Level Languages",
                    items: [
                        "Assembly",
                        "C",
                        "อื่นๆ"
                    ]
                },
                midLevelLanguages: {
                    title: "Mid-Level Languages",
                    items: [
                        "C++", "Rust",
                        "Ada",
                        "อื่นๆ"
                    ]
                },
                highLevelLanguages: {
                    title: "High-Level Languages",
                    items: [
                        "Python",
                        "Java",
                        "JavaScript",
                        "Lua",
                        "PHP",
                        "Ruby",
                        "C#",
                        "อื่นๆ"
                    ]
                },
                specializedSoftware: {
                    title: "Specialized Software",
                    items: [
                        "Accounting Software",
                        "HR Software",
                        "CRM Software",
                        "ERP Software",
                        "Security Software",
                        "อื่นๆ"
                    ]
                },
                securitySoftware: {
                    title: "Security Software",
                    items: [
                        "Anti-virus",
                        "Backup Software",
                        "อื่นๆ"
                    ]
                }
            }
        },
        dataInfrastructure: {
            title: "4. Data-Infrastructure",
            categories: {
                dataStorage: {
                    title: "การจัดเก็บข้อมูล",
                    items: [
                        "On-premise Storage",
                        "Cloud Storage",
                        "Backup System"
                    ]
                },
                database: {
                    title: "Database",
                    items: [
                        "RDBMS",
                        "NoSQL",
                        "Data Warehouse"
                    ]
                },
                dataAnalytics: {
                    title: "การวิเคราะห์ข้อมูล",
                    items: [
                        "BI Tools",
                        "Data Analytics",
                        "Reporting Tools"
                    ]
                }
            }
        }
    };

    const renderAssessmentItems = (sectionKey, categoryKey, items) => {
        // For software section, render with additional license and version fields
        if (sectionKey === 'software') {
            return items.map((item, index) => (
                <tr key={`${sectionKey}-${categoryKey}-${index}`} className="border-b">
                    <td className="p-2 border-r">{item}</td>
                    <td className="p-2 border-r">
                        <div className="flex flex-col space-y-2">
                            <input
                                type="text"
                                className="w-full p-1 border"
                                placeholder="รายละเอียด"
                                value={formData[sectionKey]?.[categoryKey]?.[item]?.details || ''}
                                onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'details', e.target.value)}
                            />
                            <div className="flex space-x-2">
                                <input
                                    type="text"
                                    className="w-1/2 p-1 border"
                                    placeholder="จำนวนลิขสิทธิ์"
                                    value={formData[sectionKey]?.[categoryKey]?.[item]?.licenses || ''}
                                    onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'licenses', e.target.value)}
                                />
                                <input
                                    type="text"
                                    className="w-1/2 p-1 border"
                                    placeholder="Version"
                                    value={formData[sectionKey]?.[categoryKey]?.[item]?.version || ''}
                                    onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'version', e.target.value)}
                                />
                            </div>
                        </div>
                    </td>
                    <td className="p-2 border-r">
                        <ScoreBar
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.currentStatus || 0}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                            field="currentStatus"
                        />
                    </td>
                    <td className="p-2 border-r">
                        <ScoreBar
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.target || 0}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                            field="target"
                        />
                    </td>
                    <td className="p-2 border-r">
                        <PrioritySelect
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.priority || ''}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="number"
                            className="w-full p-1 border"
                            placeholder="เดือน"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.age || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'age', e.target.value)}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="เป้าหมาย"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.targetDescription || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'targetDescription', e.target.value)}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="ข้อเสนอแนะ"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.suggestions || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'suggestions', e.target.value)}
                        />
                    </td>
                    <td className="p-2">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="ผู้รับผิดชอบ"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.responsible || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'responsible', e.target.value)}
                        />
                    </td>
                </tr>
            ));
        } else if (sectionKey === 'hardware') {
            // For hardware section, render without the "ผู้รับผิดชอบ" column
            return items.map((item, index) => (
                <tr key={`${sectionKey}-${categoryKey}-${index}`} className="border-b">
                    <td className="p-2 border-r">{item}</td>
                    <td className="p-2 border-r">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="รายละเอียด"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.details || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'details', e.target.value)}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <ScoreBar
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.currentStatus || 0}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                            field="currentStatus"
                        />
                    </td>
                    <td className="p-2 border-r">
                        <ScoreBar
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.target || 0}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                            field="target"
                        />
                    </td>
                    <td className="p-2 border-r">
                        <PrioritySelect
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.priority || ''}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="number"
                            className="w-full p-1 border"
                            placeholder="เดือน"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.age || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'age', e.target.value)}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="เป้าหมาย"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.targetDescription || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'targetDescription', e.target.value)}
                        />
                    </td>
                    <td className="p-2">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="ข้อเสนอแนะ"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.suggestions || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'suggestions', e.target.value)}
                        />
                    </td>
                </tr>
            ));
        } else {
            // For other sections, use the original rendering
            return items.map((item, index) => (
                <tr key={`${sectionKey}-${categoryKey}-${index}`} className="border-b">
                    <td className="p-2 border-r">{item}</td>
                    <td className="p-2 border-r">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="รายละเอียด"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.details || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'details', e.target.value)}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <ScoreBar
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.currentStatus || 0}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                            field="currentStatus"
                        />
                    </td>
                    <td className="p-2 border-r">
                        <ScoreBar
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.target || 0}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                            field="target"
                        />
                    </td>
                    <td className="p-2 border-r">
                        <PrioritySelect
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.priority || ''}
                            onChange={handleInputChange}
                            section={sectionKey}
                            category={categoryKey}
                            item={item}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="number"
                            className="w-full p-1 border"
                            placeholder="เดือน"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.age || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'age', e.target.value)}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="เป้าหมาย"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.targetDescription || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'targetDescription', e.target.value)}
                        />
                    </td>
                    <td className="p-2 border-r">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="ข้อเสนอแนะ"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.suggestions || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'suggestions', e.target.value)}
                        />
                    </td>
                    <td className="p-2">
                        <input
                            type="text"
                            className="w-full p-1 border"
                            placeholder="ผู้รับผิดชอบ"
                            value={formData[sectionKey]?.[categoryKey]?.[item]?.responsible || ''}
                            onChange={(e) => handleInputChange(sectionKey, categoryKey, item, 'responsible', e.target.value)}
                        />
                    </td>
                </tr>
            ));
        }
    };

    const renderSummary = () => {
        return (
            <div className="p-4">
                <h2 className="text-xl font-bold mb-4">6. ข้อมูลการประเมิน</h2>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
                        <strong className="font-bold">Error! </strong>
                        <span className="block sm:inline">{error}</span>
                    </div>
                )}

                {successMessage && (
                    <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">
                        <strong className="font-bold">Success! </strong>
                        <span className="block sm:inline">{successMessage}</span>
                    </div>
                )}

                {formData.id && (
                    <div className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative mb-4">
                        <strong className="font-bold">Editing Assessment: </strong>
                        <span className="block sm:inline">ID #{formData.id}</span>
                    </div>
                )}

                <div className="border p-4 rounded mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block mb-1 font-semibold">ชื่อการประเมิน: <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded"
                                value={formData.assessmentName}
                                onChange={(e) => handleMetadataChange('assessmentName', e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className="block mb-1 font-semibold">ชื่อบริษัท: <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded"
                                value={formData.companyName}
                                onChange={(e) => handleMetadataChange('companyName', e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block mb-1 font-semibold">ชื่อ-นามสกุล: <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded"
                                value={formData.assessorName}
                                onChange={(e) => handleMetadataChange('assessorName', e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className="block mb-1 font-semibold">ตำแหน่ง: <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                className="w-full p-2 border rounded"
                                value={formData.position}
                                onChange={(e) => handleMetadataChange('position', e.target.value)}
                                required
                            />
                        </div>
                        <div>
                            <label className="block mb-1 font-semibold">วันที่ประเมิน: <span className="text-red-500">*</span></label>
                            <input
                                type="date"
                                className="w-full p-2 border rounded"
                                value={formData.assessmentDate}
                                onChange={(e) => handleMetadataChange('assessmentDate', e.target.value)}
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-6">
                    <h3 className="text-lg font-bold mb-2">Notes:</h3>
                    <textarea
                        className="w-full p-2 border rounded"
                        rows="3"
                        placeholder="หมายเหตุหรือคำอธิบายเพิ่มเติมเกี่ยวกับการประเมิน"
                        value={formData.notes}
                        onChange={(e) => handleMetadataChange('notes', e.target.value)}
                    ></textarea>
                </div>

                <div className="mt-6 flex justify-end">
                    <button
                        className="px-6 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                        onClick={handleSaveAssessment}
                        disabled={saving}
                    >
                        {saving ? 'กำลังบันทึก...' : 'บันทึกการประเมิน'}
                    </button>
                </div>
            </div>
        );
    };

    const renderBudgetSection = () => {
        // Calculate total budget
        const totalBudget = calculateTotalBudget();

        return (
            <div className="p-4">
                <h2 className="text-xl font-bold mb-4">5. งบประมาณ</h2>
                <table className="w-full border">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="p-2 border">หมวด</th>
                            <th className="p-2 border">งบประมาณ (บาท)</th>
                            <th className="p-2 border">รายละเอียด</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b">
                            <td className="p-2 border">Hardware</td>
                            <td className="p-2 border">
                                <input
                                    type="number"
                                    className="w-full p-1 border"
                                    placeholder="0.00"
                                    value={formData.budget.hardware}
                                    onChange={(e) => handleBudgetChange('hardware', e.target.value)}
                                    onBlur={() => calculateTotalBudget()}
                                />
                            </td>
                            <td className="p-2 border">
                                <input
                                    type="text"
                                    className="w-full p-1 border"
                                    placeholder="รายละเอียดงบประมาณ Hardware"
                                    value={formData.budget.hardwareDetails}
                                    onChange={(e) => handleBudgetChange('hardwareDetails', e.target.value)}
                                />
                            </td>
                        </tr>
                        <tr className="border-b">
                            <td className="p-2 border">Network</td>
                            <td className="p-2 border">
                                <input
                                    type="number"
                                    className="w-full p-1 border"
                                    placeholder="0.00"
                                    value={formData.budget.network}
                                    onChange={(e) => handleBudgetChange('network', e.target.value)}
                                    onBlur={() => calculateTotalBudget()}
                                />
                            </td>
                            <td className="p-2 border">
                                <input
                                    type="text"
                                    className="w-full p-1 border"
                                    placeholder="รายละเอียดงบประมาณ Network"
                                    value={formData.budget.networkDetails}
                                    onChange={(e) => handleBudgetChange('networkDetails', e.target.value)}
                                />
                            </td>
                        </tr>
                        <tr className="border-b">
                            <td className="p-2 border">Software</td>
                            <td className="p-2 border">
                                <input
                                    type="number"
                                    className="w-full p-1 border"
                                    placeholder="0.00"
                                    value={formData.budget.software}
                                    onChange={(e) => handleBudgetChange('software', e.target.value)}
                                    onBlur={() => calculateTotalBudget()}
                                />
                            </td>
                            <td className="p-2 border">
                                <input
                                    type="text"
                                    className="w-full p-1 border"
                                    placeholder="รายละเอียดงบประมาณ Software"
                                    value={formData.budget.softwareDetails}
                                    onChange={(e) => handleBudgetChange('softwareDetails', e.target.value)}
                                />
                            </td>
                        </tr>
                        <tr className="border-b">
                            <td className="p-2 border">Data-Infrastructure</td>
                            <td className="p-2 border">
                                <input
                                    type="number"
                                    className="w-full p-1 border"
                                    placeholder="0.00"
                                    value={formData.budget.dataInfrastructure}
                                    onChange={(e) => handleBudgetChange('dataInfrastructure', e.target.value)}
                                    onBlur={() => calculateTotalBudget()}
                                />
                            </td>
                            <td className="p-2 border">
                                <input
                                    type="text"
                                    className="w-full p-1 border"
                                    placeholder="รายละเอียดงบประมาณ Data-Infrastructure"
                                    value={formData.budget.dataInfrastructureDetails}
                                    onChange={(e) => handleBudgetChange('dataInfrastructureDetails', e.target.value)}
                                />
                            </td>
                        </tr>
                        <tr className="bg-gray-100 font-bold">
                            <td className="p-2 border">รวมทั้งสิ้น</td>
                            <td className="p-2 border">
                                {parseFloat(totalBudget).toLocaleString()} บาท
                            </td>
                            <td className="p-2 border"></td>
                        </tr>
                    </tbody>
                </table>

                <div className="mt-6 bg-gray-50 p-4 rounded">
                    <h3 className="font-bold mb-2">หมายเหตุ:</h3>
                    <textarea
                        className="w-full p-2 border rounded"
                        rows="3"
                        placeholder="หมายเหตุหรือคำอธิบายเพิ่มเติมเกี่ยวกับงบประมาณ"
                    ></textarea>
                </div>
            </div>
        );
    };

    const renderScoreDescription = () => {
        return (
            <div className="mt-6 p-4 bg-gray-50 rounded">
                <h3 className="font-bold mb-2">คำอธิบายคะแนน:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <p className="font-semibold">ค่าคะแนนในการประเมิน:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>1 = ไม่เพียงพอ/ล้าสมัยมาก ต้องเปลี่ยนทันที</li>
                            <li>2 = ไม่เพียงพอ/ล้าสมัย ควรปรับปรุง</li>
                            <li>3 = เพียงพอ/ใช้งานได้ แต่ควรวางแผนปรับปรุง</li>
                            <li>4 = ดี/ทันสมัย ตรงตามความต้องการ</li>
                            <li>5 = ดีมาก/ทันสมัยมาก เกินความต้องการปัจจุบัน</li>
                        </ul>
                    </div>
                    <div>
                        <p className="font-semibold">ความเร่งด่วน:</p>
                        <ul className="list-disc pl-5 space-y-1">
                            <li>H = ต้องดำเนินการทันที (1-3 เดือน)</li>
                            <li>M = ควรดำเนินการเร็วๆ นี้ (3-6 เดือน)</li>
                            <li>L = สามารถรอได้ (6-12 เดือน)</li>
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    const renderSection = (sectionKey) => {
        const section = sections[sectionKey];

        return (
            <div className="p-4">
                <h2 className="text-xl font-bold mb-4">{section.title}</h2>

                {Object.entries(section.categories).map(([categoryKey, category]) => (
                    <div key={categoryKey} className="mb-8">
                        <h3 className="text-lg font-semibold mb-2">{category.title}</h3>
                        <table className="w-full border">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="p-2 border">รายการประเมิน</th>
                                    <th className="p-2 border">รายละเอียด</th>
                                    <th className="p-2 border">สถานะปัจจุบัน</th>
                                    <th className="p-2 border">เป้าหมาย</th>
                                    <th className="p-2 border">ความเร่งด่วน</th>
                                    <th className="p-2 border">อายุการใช้งาน (เดือน)</th>
                                    <th className="p-2 border">เป้าหมาย</th>
                                    <th className="p-2 border">ข้อเสนอแนะ</th>
                                    {sectionKey !== 'hardware' && <th className="p-2 border">ผู้รับผิดชอบ</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {renderAssessmentItems(sectionKey, categoryKey, category.items)}
                            </tbody>
                        </table>
                    </div>
                ))}

                {renderScoreDescription()}
            </div>
        );
    };

    return (
        <div className="max-w-full bg-white rounded shadow">
            <div className="p-4 bg-blue-600 text-white">
                <h1 className="text-2xl font-bold">แบบประเมินความพร้อมองค์กรสำหรับ Digital Transformation</h1>
                {formData.id && (
                    <p className="mt-2 text-white bg-blue-700 inline-block px-3 py-1 rounded">
                        กำลังแก้ไขแบบประเมิน ID: {formData.id}
                    </p>
                )}
            </div>

            <div className="bg-gray-100 p-2 flex overflow-x-auto">
                <button
                    className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'hardware' ? 'bg-white' : 'bg-gray-200'}`}
                    onClick={() => handleTabChange('hardware')}
                >
                    1. Hardware
                </button>
                <button
                    className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'network' ? 'bg-white' : 'bg-gray-200'}`}
                    onClick={() => handleTabChange('network')}
                >
                    2. Network
                </button>
                <button
                    className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'software' ? 'bg-white' : 'bg-gray-200'}`}
                    onClick={() => handleTabChange('software')}
                >
                    3. Software
                </button>
                <button
                    className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'dataInfrastructure' ? 'bg-white' : 'bg-gray-200'}`}
                    onClick={() => handleTabChange('dataInfrastructure')}
                >
                    4. Data-Infrastructure
                </button>
                <button
                    className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'budget' ? 'bg-white' : 'bg-gray-200'}`}
                    onClick={() => handleTabChange('budget')}
                >
                    5. งบประมาณ
                </button>
                <button
                    className={`px-4 py-2 font-semibold rounded-t ${activeTab === 'summary' ? 'bg-white' : 'bg-gray-200'}`}
                    onClick={() => handleTabChange('summary')}
                >
                    6. ข้อมูลการประเมิน
                </button>
            </div>

            <div className="overflow-x-auto">
                {activeTab === 'hardware' && renderSection('hardware')}
                {activeTab === 'network' && renderSection('network')}
                {activeTab === 'software' && renderSection('software')}
                {activeTab === 'dataInfrastructure' && renderSection('dataInfrastructure')}
                {activeTab === 'summary' && renderSummary()}
                {activeTab === 'budget' && renderBudgetSection()}
            </div>
        </div>
    );
};

export default DigitalTransformationAssessment;